import React, { useEffect, useSyncExternalStore } from "react";
import { useIntl } from "react-intl";

/**
 * Upload fields the API last rejected as corrupted. Its `CORRUPTED_FILE`
 * response lists the multer field names in `fields`; AxiosConfig records them
 * here so each page can show the message under the matching upload control,
 * whatever its saga does with the error.
 */
let rejectedFields = [];
const listeners = new Set();

const emit = () => listeners.forEach((listener) => listener());

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const setCorruptedUploadFields = (fields) => {
  rejectedFields = Array.isArray(fields) ? fields : [];
  emit();
};

export const clearCorruptedUpload = (field) => {
  if (!rejectedFields.includes(field)) return;
  rejectedFields = rejectedFields.filter((name) => name !== field);
  emit();
};

export const useCorruptedUpload = (field) => {
  const rejected = useSyncExternalStore(subscribe, () =>
    rejectedFields.includes(field),
  );
  // A rejection must not follow the user to another page using the same
  // field name (e.g. `photo`).
  useEffect(() => () => clearCorruptedUpload(field), [field]);
  return rejected;
};

/**
 * Inline, translated "file is corrupted" message for one upload control.
 * Shows when the API rejected `field`, or when `show` is set because the
 * browser itself could not read the picked image.
 */
export function CorruptedUploadError({ field, show = false, className = "" }) {
  const intl = useIntl();
  const rejected = useCorruptedUpload(field);
  if (!show && !rejected) return null;
  return (
    <div className={`text-danger font-size-sm mt-1 ${className}`}>
      {intl.formatMessage({
        id: "UPLOAD.FILE.CORRUPTED",
        defaultMessage:
          "The file is corrupted or invalid. Please upload a valid file.",
      })}
    </div>
  );
}
